import React, {useEffect, useState} from "react";
import moment from "moment";
import {Box, Skeleton, Typography} from "@mui/material";
import {ProjectStatisticRepository} from "../../../repositories/ProjectStatisticRepository";
import {Sparkline} from "../../registry-project/dashboard/components/Sparkline";

const DATE_TIME = "YYYY-MM-DDTHH:mm:ss";
const REFRESH_INTERVAL = 60000;
const WIDTH = 190;
const HEIGHT = 96;

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

// Peak concurrent calls of the whole project (every language) per hour over the last 24 hours,
// read from the project's own backend and refreshed every minute
export const ProjectActiveCallsChart = ({project}) => {
  const [statistic, setStatistic] = useState(null);
  const [failed, setFailed] = useState(false);
  const baseURL = project?.backendConfiguration?.conversationLoggerApi || process.env.REACT_APP_CHATBOT_BACKEND;

  useEffect(() => {
    if (!project?.id) return;
    let controller = null;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const end = moment();
        const response = await ProjectStatisticRepository.getResponseTime({
          projectId: project.id,
          type: "ACTIVE_CALLS",
          period: "HOURLY",
          start: end.clone().subtract(24, "hours").format(DATE_TIME),
          end: end.format(DATE_TIME),
          signal: controller.signal,
          baseURL,
        });
        setStatistic(response?.data || {items: []});
        setFailed(false);
      } catch (error) {
        if (!isCancel(error)) {
          console.error(`Error fetching active calls of project ${project.id}:`, error);
          setFailed(true);
        }
      }
    };
    load();
    const intervalId = setInterval(load, REFRESH_INTERVAL);
    return () => {
      clearInterval(intervalId);
      controller?.abort();
    };
  }, [project?.id, baseURL]);

  if (!statistic && !failed) {
    return <Skeleton variant={"rounded"} width={WIDTH} height={HEIGHT} sx={{mx: "auto", borderRadius: 2}}/>;
  }

  const points = (statistic?.items || []).map(item => ({x: item.timestamp, y: item.max}));
  const peak = Math.round(Number(statistic?.max) || 0);

  return (
    <Box sx={{width: WIDTH, mx: "auto"}}>
      {points.some(point => Number(point.y) > 0) ? (
        <Sparkline
          name={"Active calls"}
          points={points}
          color={"#8491F7"}
          height={HEIGHT}
          decimals={0}
          showAxis={false}
          formatX={(x) => moment(x).format("DD MMM HH:mm")}
          formatY={(y) => {
            const calls = Math.round(Number(y) || 0);
            return `Peak ${calls} ${calls === 1 ? "call" : "calls"}`;
          }}
        />
      ) : (
        // flat dashed line: no calls in the last 24 hours
        <Box sx={{height: HEIGHT, display: "flex", alignItems: "center"}}>
          <Box sx={{
            width: "100%", height: "2px",
            backgroundImage: `repeating-linear-gradient(90deg, #D6D6E4 0, #D6D6E4 6px, transparent 6px, transparent 12px)`,
          }}/>
        </Box>
      )}
      <Typography sx={{mt: 0.25, fontSize: 11, color: "#8A8AA0", textAlign: "center", whiteSpace: "nowrap"}}>
        {failed ? "Active calls unavailable" : `Active calls · 24h${peak ? ` · peak ${peak}` : ""}`}
      </Typography>
    </Box>
  );
};
